/** @jsx jsx */
import { ReactNode } from 'react';
import { jsx } from '@emotion/react';
import {
  Animation,
  ColorName,
  ColorsCSS,
  ShadowName,
  Shadows,
} from '../../../theme';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

type Props = {
  /**
   * The color of the surface's background.
   *
   * @default 'transparent'
   * */
  backgroundColor?: ColorName;
  /**
   * The color of border to render around the surface.
   * */
  borderColor?: ColorName;
  /**
   * Child node to render inside the surface.
   * */
  children: ReactNode;
  /**
   * Applies a `box-shadow` with the specified shadow.
   * */
  shadow?: ShadowName;
};

const Styles = labelStyles('Surface', {
  root: (
    backgroundColor: ColorName,
    shadow: ShadowName,
    borderColor?: ColorName
  ) => [
    {
      borderRadius: 'var(--zds-radius-medium, 5px)',
      transitionProperty: 'box-shadow, border-color, background-color',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
      alignItems: 'stretch',
      flex: 1,
      maxWidth: '100%',
    },
    backgroundColor &&
      withModifier(backgroundColor, {
        backgroundColor: ColorsCSS[backgroundColor],
      }),
    borderColor &&
      withModifier(`border-${borderColor}`, {
        border: `1px solid ${ColorsCSS[borderColor]}`,
      }),
    shadow &&
      withModifier('hasShadow', {
        boxShadow: Shadows[shadow],
      }),
  ],
});
/**
 * Renders a Surface component. Surfaces represent content blocks, they are useful
 * for creating visual emphasis of content on a page. <br />
 * The Surface component acts as a "wrapping" component
 */
export const Surface = (props: Props) => {
  const { backgroundColor = 'transparent', shadow, borderColor } = props;
  return (
    <div css={Styles.root(backgroundColor, shadow, borderColor)} data-zds>
      {props.children}
    </div>
  );
};
